import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { knowledgeBaseService } from '../services/knowledgeBaseService';
import type { KnowledgeBase } from '../services/knowledgeBaseService';
import { useFullKBReviewStore } from '../stores/fullKBReviewStore';

interface Props {
  isOpen: boolean;
  subProjectId: string;
  onClose: () => void;
  onLaunched: (reviewId: string, transcriptId: string) => void;
}

type Step = 'select_kb' | 'confirm_cost' | 'launching';

const FullKBReviewLaunchModal: React.FC<Props> = ({
  isOpen,
  subProjectId,
  onClose,
  onLaunched,
}) => {
  const { t } = useTranslation();
  const { fetchEstimate, startReview, costEstimate, isLoadingEstimate, isLaunching, error, reset } = useFullKBReviewStore();

  const [step, setStep] = useState<Step>('select_kb');
  const [availableKBs, setAvailableKBs] = useState<KnowledgeBase[]>([]);
  const [selectedKbId, setSelectedKbId] = useState<string>('');
  const [loadingKBs, setLoadingKBs] = useState(false);

  // Load KBs on open
  useEffect(() => {
    if (!isOpen) return;
    reset();
    setStep('select_kb');
    setSelectedKbId('');

    const loadKBs = async () => {
      setLoadingKBs(true);
      try {
        const all = await knowledgeBaseService.getAll();
        setAvailableKBs(all);
        if (all.length === 1) setSelectedKbId(all[0].id);
      } catch (_) {
        // show empty list
      } finally {
        setLoadingKBs(false);
      }
    };
    loadKBs();
  }, [isOpen]);

  const handleEstimate = async () => {
    if (!selectedKbId) return;
    await fetchEstimate(subProjectId, selectedKbId);
    setStep('confirm_cost');
  };

  const handleLaunch = async () => {
    if (!selectedKbId) return;
    setStep('launching');
    try {
      const launch = await startReview(subProjectId, selectedKbId);
      onLaunched(launch.review_id, launch.transcript_id);
    } catch (_) {
      setStep('confirm_cost');
    }
  };

  const formatCost = (cents: number, currency: string) => {
    const amount = cents / 100;
    return `${currency === 'EUR' ? '€' : '$'}${amount.toFixed(2)}`;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-base font-semibold">{t('fullKbReview.modalTitle', 'Full KB Review')}</h2>
          <span className="text-slate-400 hover:text-slate-600 text-xl cursor-pointer" onClick={onClose}>&times;</span>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">

          {/* Step 1: Select KB */}
          {step === 'select_kb' && (
            <>
              <p className="text-sm text-slate-600">
                {t('fullKbReview.selectKBHint', 'Select the Knowledge Base to review. All documents with available text will be assessed.')}
              </p>
              {loadingKBs ? (
                <p className="text-sm text-slate-400">{t('common.loading', 'Loading...')}</p>
              ) : availableKBs.length === 0 ? (
                <p className="text-sm text-slate-500">
                  {t('fullKbReview.noKBLinked', 'No Knowledge Base available.')}
                </p>
              ) : (
                <select
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={selectedKbId}
                  onChange={(e) => setSelectedKbId(e.target.value)}
                >
                  <option value="" disabled>{t('fullKbReview.selectKBPlaceholder', 'Select a Knowledge Base...')}</option>
                  {availableKBs.map(kb => (
                    <option key={kb.id} value={kb.id}>
                      {kb.name} ({kb.document_count} {t('fullKbReview.docs', 'docs')})
                    </option>
                  ))}
                </select>
              )}
            </>
          )}

          {/* Step 2: Cost confirmation */}
          {step === 'confirm_cost' && costEstimate && (
            <>
              <p className="text-sm font-medium">{t('fullKbReview.costEstimateTitle', 'Cost Estimate')}</p>
              <div className="bg-slate-50 rounded p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span>{t('fullKbReview.costPatents', 'Patents')}</span>
                  <span>{costEstimate.patent_count}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('fullKbReview.costOther', 'Other documents')}</span>
                  <span>{costEstimate.other_count}</span>
                </div>
                {costEstimate.skipped_count > 0 && (
                  <div className="flex justify-between text-amber-600">
                    <span>{t('fullKbReview.costSkipped', 'No text available (will be skipped)')}</span>
                    <span>{costEstimate.skipped_count}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-slate-200 pt-1 mt-1">
                  <span>{t('fullKbReview.costEstRelevant', 'Estimated relevant documents')}</span>
                  <span>~{costEstimate.estimated_relevant}</span>
                </div>
                <div className="flex justify-between font-semibold text-base pt-1">
                  <span>{t('fullKbReview.costEstimated', 'Estimated cost')}</span>
                  <span>{formatCost(costEstimate.estimated_cost_cents, costEstimate.currency)}</span>
                </div>
                <p className="text-xs text-slate-400 pt-1">
                  {t('fullKbReview.costModel', 'Model:')} {costEstimate.model_name || costEstimate.model_id}
                </p>
              </div>
              <p className="text-xs text-slate-400">
                {t('fullKbReview.costDisclaimer', 'This is an estimate based on average document sizes. Actual cost may vary.')}
              </p>
            </>
          )}

          {/* Launching */}
          {step === 'launching' && (
            <p className="text-sm text-slate-600">{t('fullKbReview.launching', 'Launching review...')}</p>
          )}

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200 flex-shrink-0">
          <button
            type="button"
            className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
            onClick={onClose}
            disabled={step === 'launching'}
          >
            {t('common.cancel', 'Cancel')}
          </button>

          {step === 'select_kb' && (
            <button
              type="button"
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
              onClick={handleEstimate}
              disabled={!selectedKbId || isLoadingEstimate}
            >
              {isLoadingEstimate
                ? t('fullKbReview.estimating', 'Estimating...')
                : t('fullKbReview.nextEstimate', 'Estimate Cost')}
            </button>
          )}

          {step === 'confirm_cost' && (
            <>
              <button
                type="button"
                className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
                onClick={() => setStep('select_kb')}
              >
                {t('common.back', 'Back')}
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                onClick={handleLaunch}
                disabled={isLaunching}
              >
                {isLaunching
                  ? t('fullKbReview.launching', 'Launching...')
                  : t('fullKbReview.confirmLaunch', 'Confirm & Start Review')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FullKBReviewLaunchModal;
